export const DAY_MS = 86400000;

/** Midnight UTC of the day containing `ms`. */
export const utcDayStart = ms => Math.floor(ms / DAY_MS) * DAY_MS;

/**
 * Where the delivery record starts: 2026-09-07 00:00 UTC.
 *
 * Before that the settled rows are not trustworthy — the fleet-wide statistics job was blind
 * to its sample store until 2026-09-07T11:00Z and settled every battery with hard zeros and
 * both device lists empty, and the corrections that followed only covered some batteries.
 * Nothing before this instant is shown; there is no synthetic backfill.
 */
export const RECORD_STARTS_UTC = Date.UTC(2026, 8, 7);

/**
 * `[now − days, now]` as ISO strings, with `from` clamped to the record start.
 *
 * @param {number} days
 * @param {number} [nowMs]
 * @returns {{from: string, to: string, clamped: boolean}}
 */
export const pastRange = (days, nowMs = Date.now()) => {
    const wanted = nowMs - days * DAY_MS;
    const clamped = wanted < RECORD_STARTS_UTC;
    return {
        from: new Date(clamped ? RECORD_STARTS_UTC : wanted).toISOString(),
        to: new Date(nowMs).toISOString(),
        clamped,
    };
};

/**
 * Split `[from, to]` into contiguous sub-windows, none of which can hold more than `maxRows`
 * settled reports. The server caps one listing at 1000 rows with no paging, so a wide window
 * has to be fetched as several requests. Internal boundaries are snapped to UTC midnight: the
 * server selects reports fully contained in the window, so a cut inside a period would drop
 * it from both neighbours, and every settlement period divides a day.
 *
 * @param {string} fromIso
 * @param {string} toIso
 * @param {{periodMinutes?: number, maxRows?: number}} [opts]
 * @returns {Array<{from: string, to: string}>}
 */
export const DELIVERED_MAX_LIMIT = 1000;

export const chunkDeliveryWindow = (fromIso, toIso, opts = {}) => {
    const from = Date.parse(fromIso);
    const to = Date.parse(toIso);
    if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) {
        return [];
    }

    const periodMinutes = opts.periodMinutes ?? 15;
    const maxRows = opts.maxRows ?? DELIVERED_MAX_LIMIT;
    const rowsPerDay = Math.ceil(1440 / Math.max(1, periodMinutes));
    // `maxRows - 1`: coming back with exactly `maxRows` rows is the only signal of truncation,
    // so a chunk sized to hit that number exactly would raise a false alarm.
    const daysPerChunk = Math.max(1, Math.floor((maxRows - 1) / rowsPerDay));
    const span = daysPerChunk * DAY_MS;

    if (to - from <= span) {
        return [{ from: fromIso, to: toIso }];
    }

    const bounds = [from];
    let t = Math.ceil(from / DAY_MS) * DAY_MS;
    if (t <= from) {
        t = from + span;
    }
    for (; t < to; t += span) {
        bounds.push(t);
    }
    bounds.push(to);

    const chunks = [];
    for (let i = 0; i < bounds.length - 1; i++) {
        chunks.push({ from: new Date(bounds[i]).toISOString(), to: new Date(bounds[i + 1]).toISOString() });
    }
    return chunks;
};
